import type { IdentifierChannel } from '../auth/session';

/**
 * "Complete your profile".
 * Sign-up is folded into sign-in: Core creates the account from one identifier, so a new
 * person arrives with no name and only an email *or* a phone. That one identifier is enough
 * to have an account (PRD-1958): the profile is complete with a first name, a last name and
 * the identifier they signed in with. The other one can be added on the screen or later in
 * the profile, but never has to be. (OnEarly `app` asks for both plus a date of birth; the
 * Figma asks for neither.)
 */
export interface ProfileShape {
  firstName: string | null | undefined;
  lastName: string | null | undefined;
  email: string | null | undefined;
  phoneNumber: string | null | undefined;
}

const present = (value: string | null | undefined): value is string =>
  typeof value === 'string' && value.trim().length > 0;

export function isProfileComplete(profile: ProfileShape): boolean {
  return (
    present(profile.firstName) &&
    present(profile.lastName) &&
    (present(profile.email) || present(profile.phoneNumber))
  );
}

/** The identifier the person may still add — the one they did *not* sign in with. */
export function missingIdentifier(profile: ProfileShape): IdentifierChannel | undefined {
  if (!present(profile.phoneNumber)) return 'phone';
  if (!present(profile.email)) return 'email';
  return undefined;
}

/**
 * What a code was sent to, without exposing it fully (OnEarly `app`): `ja**@example.com`,
 * `+61 **** **** 123`.
 */
export function maskIdentifier(channel: IdentifierChannel, identifier: string): string {
  if (channel === 'email') {
    const [local, domain] = identifier.split('@');
    if (!local || !domain) return identifier;
    return `${local.slice(0, 2)}${'*'.repeat(Math.max(local.length - 2, 1))}@${domain}`;
  }
  const digits = identifier.replace(/\D/g, '');
  const last = digits.slice(-3);
  const dial = /^\+(\d{1,3})/.exec(identifier);
  return dial ? `+${dial[1]} **** **** ${last}` : `*** *** ${last}`;
}

/** The identifier belongs to another OnEarly account: the person has to use a different one. */
export class IdentifierInUseError extends Error {
  constructor(readonly channel: IdentifierChannel) {
    super(`${channel} already in use by another account`);
    this.name = 'IdentifierInUseError';
  }
}

const statusOf = (error: unknown): number | undefined =>
  typeof error === 'object' && error !== null && 'status' in error
    ? ((error as { status?: unknown }).status as number | undefined)
    : undefined;

export interface IdentifierVerificationApi<Flow> {
  /** `POST /users/me/phones|emails` — adds the identifier and sends the code. */
  add: () => Promise<Flow | undefined>;
  /** `POST /users/me/phones|emails/resend` — a new code for an identifier already on the account. */
  resend: () => Promise<Flow | undefined>;
}

/**
 * Adds the missing identifier and gets a verification flow back, however far a previous
 * attempt got. Core answers 409 both when the identifier is already on *this* account
 * (added earlier, never verified — the person closed the app at the code step) and when it
 * belongs to another one; `resend` tells them apart: it succeeds for the former and 404s for
 * the latter. A flow can also be missing from a 200 when the SMS/email could not be sent —
 * the identifier was still added, so a resend is the retry.
 */
export async function startIdentifierVerification<Flow>(
  api: IdentifierVerificationApi<Flow>,
  channel: IdentifierChannel,
): Promise<Flow> {
  let flow: Flow | undefined;
  try {
    flow = await api.add();
  } catch (error) {
    if (statusOf(error) !== 409) throw error;
    try {
      flow = await api.resend();
    } catch (inner) {
      if (statusOf(inner) === 404) throw new IdentifierInUseError(channel);
      throw inner;
    }
  }
  if (!flow) flow = await api.resend();
  if (!flow) throw new Error('OnEarly: no verification flow was returned');
  return flow;
}
